import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import StockChart from '../../Charts/StockChart';
import { useTrendLineDrawing } from '../../../contexts/TrendLineDrawingContext';
import { useTheme } from '../../../contexts/ThemeContext';

// SVG Icons
const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const UpIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

const DownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0v-8m0 8l-8-8-4 4-6-6" />
  </svg>
);

const TrendLineIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
  </svg>
);

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

/**
 * TrendLineChartModal - Modal for drawing trend lines on a chart
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether modal is open
 * @param {Function} props.onClose - Close handler
 * @param {string} props.symbol - Stock symbol to display
 * @param {Function} props.onSelectTrendLine - Callback when trendline is selected
 */
const TrendLineChartModal = ({
  open,
  onClose,
  symbol = 'TITAN',
  onSelectTrendLine,
}) => {
  const { addTrendLine, drawnTrendLines, getTrendLinesBySymbol } = useTrendLineDrawing();
  const { isDark } = useTheme();

  // Local state for trendlines drawn in this session
  const [chartTrendLines, setChartTrendLines] = useState([]);
  const [selectedLineId, setSelectedLineId] = useState(null);

  // Get trendlines for current symbol
  const symbolTrendLines = getTrendLinesBySymbol(symbol);

  // Handle trendlines change from StockChart
  const handleTrendLinesChange = useCallback((lines) => {
    console.log('[TrendLineChartModal] Trend lines from chart:', lines);
    setChartTrendLines(lines);
  }, []);

  // Handle selecting a trendline from the list
  const handleSelectLine = (line) => {
    setSelectedLineId(line.id);
  };

  // Handle confirming selection
  const handleConfirmSelection = () => {
    const selectedLine = chartTrendLines.find(l => l.id === selectedLineId)
      || symbolTrendLines.find(l => l.id === selectedLineId);

    if (selectedLine) {
      // Add to context if it's a new line from chart
      const isNewLine = chartTrendLines.some(l => l.id === selectedLineId);
      if (isNewLine) {
        addTrendLine({
          ...selectedLine,
          symbol: symbol,
        });
      }

      // Call the callback with the selected trendline
      if (onSelectTrendLine) {
        onSelectTrendLine({
          id: selectedLine.id,
          name: selectedLine.name || `Trend Line`,
          x1: selectedLine.x1,
          y1: selectedLine.y1,
          x2: selectedLine.x2,
          y2: selectedLine.y2,
          symbol: symbol,
        });
      }
      onClose();
    }
  };

  // Get trend direction
  const getTrendDirection = (line) => {
    if (!line?.y1 || !line?.y2) return null;
    return line.y2 > line.y1 ? 'up' : 'down';
  };

  // Format price for display
  const formatPrice = (price) => {
    if (price === null || price === undefined) return 'N/A';
    return `₹${price.toFixed(2)}`;
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Reset selection when modal opens
  useEffect(() => {
    if (open) {
      setSelectedLineId(null);
      setChartTrendLines([]);
    }
  }, [open]);

  // Combine chart trendlines and previously drawn trendlines
  const allTrendLines = [
    ...chartTrendLines,
    ...symbolTrendLines.filter(sl => !chartTrendLines.some(cl => cl.id === sl.id)),
  ];

  if (!open) return null;

  return createPortal(
    <dialog className="modal modal-open">
      <div className="modal-box h-[90vh] p-0 flex flex-col" style={{ maxWidth: 'none', width: '90vw' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-base-300">
          <div className="flex items-center gap-2">
            <span className="text-primary"><TrendLineIcon /></span>
            <h3 className="font-bold text-lg">Draw Trend Line - {symbol}</h3>
          </div>
          <button className="btn btn-sm btn-circle btn-ghost" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Instructions */}
          <div className="alert alert-info m-4 mb-0">
            <span className="text-sm">
              <strong>Instructions:</strong> Click "Trend Line" button below, then click and drag on the chart to draw a trend line.
              Select the drawn line from the list and click "Use Selected Trend Line".
            </span>
          </div>

          <div className="flex flex-1 overflow-hidden">
            {/* Chart Area */}
            <div className="flex-1 p-4 overflow-hidden">
              <div className="card bg-base-100 shadow h-full p-4 flex flex-col overflow-hidden">
                <StockChart
                  symbol={symbol}
                  interval="1h"
                  theme={isDark ? 'dark' : 'light'}
                  height="100%"
                  onTrendLinesChange={handleTrendLinesChange}
                />
              </div>
            </div>

            {/* Trend Lines List */}
            <div className="w-80 border-l border-base-300 flex flex-col">
              <div className="p-4 border-b border-base-300">
                <h4 className="font-medium">Trend Lines ({allTrendLines.length})</h4>
                <p className="text-xs text-base-content/60">Draw on chart or select from list below</p>
              </div>

              {allTrendLines.length === 0 ? (
                <div className="p-6 text-center">
                  <div className="text-base-content/30 mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                    </svg>
                  </div>
                  <p className="text-sm text-base-content/60">No trend lines drawn yet</p>
                  <p className="text-xs text-base-content/40">Use the "Trend Line" button on the chart to draw</p>
                </div>
              ) : (
                <ul className="menu flex-1 overflow-auto p-0">
                  {allTrendLines.map((line, index) => {
                    const direction = getTrendDirection(line);
                    const isSelected = selectedLineId === line.id;

                    return (
                      <li key={line.id || index}>
                        <button
                          className={`flex items-start gap-2 rounded-none ${isSelected ? 'active border-l-4 border-l-primary' : ''}`}
                          onClick={() => handleSelectLine(line)}
                        >
                          <span className="mt-1">
                            {direction === 'up' ? <UpIcon /> : <DownIcon />}
                          </span>
                          <div className="flex-1 text-left">
                            <div className="flex items-center gap-1">
                              <span className={`text-sm ${isSelected ? 'font-bold' : ''}`}>
                                {line.name || `Trend Line ${index + 1}`}
                              </span>
                              {isSelected && <span className="text-primary"><CheckIcon /></span>}
                            </div>
                            <div className="text-xs text-base-content/60 mt-0.5">
                              {formatPrice(line.y1)} → {formatPrice(line.y2)}
                            </div>
                            <div className="text-xs text-base-content/60">
                              {formatTimestamp(line.x1)}
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}

              {/* Selected Line Details */}
              {selectedLineId && (
                <div className="p-4 border-t border-base-300 bg-base-200">
                  <p className="text-xs text-base-content/60 mb-2">Selected Trend Line</p>
                  {(() => {
                    const line = allTrendLines.find(l => l.id === selectedLineId);
                    if (!line) return null;
                    return (
                      <div>
                        <span className="badge badge-primary badge-sm mb-2">
                          {line.name || 'Trend Line'}
                        </span>
                        <p className="text-xs">Start: {formatPrice(line.y1)} at {formatTimestamp(line.x1)}</p>
                        <p className="text-xs">End: {formatPrice(line.y2)} at {formatTimestamp(line.x2)}</p>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-base-300">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary gap-1"
            onClick={handleConfirmSelection}
            disabled={!selectedLineId}
          >
            <CheckIcon /> Use Selected Trend Line
          </button>
        </div>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button onClick={onClose}>close</button>
      </form>
    </dialog>,
    document.body
  );
};

export default TrendLineChartModal;
